import React, { useEffect, useMemo } from "react";
import styles from "./GroupPerformance.module.scss";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { createDateRange, normalizeStatusFilter } from "features/Library/Common/utils/performanceUtils";
import { connect } from "react-redux";
import { type ConnectedProps } from "react-redux";
import GroupPerformanceBody from "./GroupPerformanceBody";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { bindAction } from "interfaces";
import { type Dispatch } from "redux";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import ReportingExport from "components/reportingExport/ReportingExport";
import { getGroupExport } from "../state/actions/groupActions";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { type RootState } from "features/Application/globaltypes/redux";

export type Props = PropsFromRedux & {
  groupId: number;
};

export const GroupPerformance: React.FC<Props> = ({ accountId, groupId, setExport, getGroupExport }) => {
  const [filter, setFilter] = usePerformanceFilter({
    accountId,
    includeAccountsDropdown: false,
    includeStatus: true,
    includePriority: true,
  });
  const normalizedFilter = useMemo(() => {
    const status = normalizeStatusFilter(filter.status ?? []);
    return { ...filter, status };
  }, [filter]);

  const reportEnabled = !!useFeatureFlag(FeatureFlags.GroupPerformance);

  const dateRange = useMemo(
    () => createDateRange(normalizedFilter.dateFrom, normalizedFilter.dateTo),
    [normalizedFilter.dateFrom, normalizedFilter.dateTo],
  );

  // Setting appropriate export method
  useEffect(() => {
    setExport({
      // @ts-ignore
      method: getGroupExport,
      args: [{ ...normalizedFilter, groupId }],
    });
  }, [normalizedFilter, setExport, groupId, getGroupExport]);

  if (!reportEnabled) {
    return (
      <div className={styles.reportUnavailable}>
        <ReportUnavailable />;
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>Summary</h2>
        <div className={styles.performanceHeader}>
          <ReportingFilter currentFilter={filter} callback={setFilter} includePriority includeStatus />
          <ReportingExport />
        </div>
      </div>
      <GroupPerformanceBody groupId={groupId} filter={normalizedFilter} dateRange={dateRange} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    getGroupExport: bindAction(getGroupExport, dispatch),
    setExport: bindAction(setExportAction, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupPerformance);
export default ConnectedComponent;
